/**
 * Service estimates API client.
 * MARCO: if AE2 already has a shared API helper (base URL + MSAL token), replace `request` below with it;
 * every call goes through this one function.
 */
import {
  DeclineReason,
  DuplicateMatch,
  LaborType,
  MarkupBand,
  ServiceCustomer,
  ServiceEstimate,
  ServiceEstimateWrite,
  ServiceSettings,
  ServiceSettingsBundle,
  TaxLocation,
} from '../interfaces/ServiceEstimate';

interface ApiConfig {
  baseUrl: string;
  // getHeaders: () => Promise<Record<string, string>>;
  getHeaders: () => any;
}

let config: ApiConfig = { baseUrl: '/service-estimates/', getHeaders: async () => ({}) };

export const configureServiceEstimatesApi = (c: Partial<ApiConfig>): void => {
  config = { ...config, ...c };
};

export class ApiError extends Error {
  status: number;
  data: Record<string, unknown>;
  constructor(status: number, data: Record<string, unknown>) {
    const detail = typeof data.detail === 'string' ? data.detail : Object.values(data).flat().join(' ');
    super(detail || 'Something went wrong. Try again.');
    this.status = status;
    this.data = data;
  }
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const headers = { 'Content-Type': 'application/json', ...(await config.getHeaders()), ...(init.headers as Record<string, string>) };
  const res = await fetch(config.baseUrl + path, { ...init, headers });
  if (!res.ok) {
    let data: Record<string, unknown> = {};
    try {
      data = await res.json();
    } catch {
      data = { detail: res.statusText };
    }
    throw new ApiError(res.status, data);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
};

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

export const serviceApi = {
  settings: () => request<ServiceSettingsBundle>('settings/'),
  saveSettings: (s: Partial<ServiceSettings>) => request<ServiceSettings>('settings/', { method: 'PUT', ...json(s) }),
  saveTaxLocation: (t: Partial<TaxLocation>) =>
    t.id ? request<TaxLocation>(`tax-locations/${t.id}/`, { method: 'PATCH', ...json(t) }) : request<TaxLocation>('tax-locations/', { method: 'POST', ...json(t) }),
  removeTaxLocation: (id: number) => request<void>(`tax-locations/${id}/`, { method: 'DELETE' }),
  allTaxLocations: () => request<TaxLocation[]>('tax-locations/'),
  saveLaborType: (t: Partial<LaborType>) => request<LaborType>(`labor-types/${t.id}/`, { method: 'PATCH', ...json(t) }),
  saveMarkupBand: (b: Partial<MarkupBand>) => request<MarkupBand>(`markup-bands/${b.id}/`, { method: 'PATCH', ...json(b) }),

  searchCustomers: (q: string) => request<ServiceCustomer[]>(`customers/?q=${encodeURIComponent(q)}`),
  pendingCustomers: () => request<ServiceCustomer[]>('customers/?status=pending'),
  /** Throws ApiError 409 with data.matches: DuplicateMatch[] when the customer may already exist. */
  createCustomer: (c: Partial<ServiceCustomer>, force = false) => request<ServiceCustomer>('customers/', { method: 'POST', ...json({ ...c, force }) }),
  updateCustomer: (id: string, c: Partial<ServiceCustomer>) => request<ServiceCustomer>(`customers/${id}/`, { method: 'PATCH', ...json(c) }),
  approveCustomer: (id: string, name?: string) => request<ServiceCustomer>(`customers/${id}/approve/`, { method: 'POST', ...json({ name }) }),
  mergeCustomer: (id: string, into: string) => request<ServiceCustomer>(`customers/${id}/merge/`, { method: 'POST', ...json({ into }) }),

  listEstimates: (scope: 'mine' | 'team' = 'mine') => request<ServiceEstimate[]>(`estimates/${scope === 'team' ? '?scope=team' : ''}`),
  getEstimate: (id: string) => request<ServiceEstimate>(`estimates/${id}/`),
  createEstimate: (e: ServiceEstimateWrite) => request<ServiceEstimate>('estimates/', { method: 'POST', ...json(e) }),
  updateEstimate: (id: string, e: Partial<ServiceEstimateWrite>) => request<ServiceEstimate>(`estimates/${id}/`, { method: 'PATCH', ...json(e) }),
  deleteDraft: (id: string) => request<void>(`estimates/${id}/`, { method: 'DELETE' }),
  emailProposal: (id: string, to: string, cc: string, message: string) =>
    request<{ detail: string }>(`estimates/${id}/email/`, { method: 'POST', ...json({ to, cc, message }) }),
  accept: (id: string, name: string, title: string, signature: string) =>
    request<ServiceEstimate>(`estimates/${id}/accept/`, { method: 'POST', ...json({ name, title, signature, agree: true }) }),
  decline: (id: string, reason: DeclineReason, note: string) =>
    request<ServiceEstimate>(`estimates/${id}/decline/`, { method: 'POST', ...json({ reason, note }) }),

  /** Returns the proposal PDF (a new version, or the signed copy once accepted). */
  proposalPdf: async (id: string): Promise<Blob> => {
    const res = await fetch(config.baseUrl + `estimates/${id}/pdf/`, { headers: await config.getHeaders() });
    if (!res.ok) {
      let data: Record<string, unknown> = {};
      try {
        data = await res.json();
      } catch {
        data = { detail: res.statusText };
      }
      throw new ApiError(res.status, data);
    }
    return res.blob();
  },
};

export type { DuplicateMatch };
